
import CTNFilterBar, { CTNFilterButton, CTNFilterDropdown, CTNFilterSearch } from '@ctn/ui/components/ctn-filter-bar';
import CTNTableComponentRow from '@ctn/ui/components/ctn-table-component-row';
import { type CTNFilterBarRef } from '@ctn/ui/types/ctn-filter-bar';
import { type CTNTableComponentRowRef } from '@ctn/ui/types/ctn-table';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import {
	type ITramitesListadoPayload,
	type ITramitesListadoResponse,
	postOperations,
} from '@/services/tramite.service';

import { PAGE_SIZE } from '@/constants/table.constants';
import { type ITramiteListado } from '@/interfaces/model/don.interface';
import { type IMaestro } from '@/interfaces/model/maestros.interface';

import getColumns, { renderSubComponent } from './columns';
import { defaultValues, type FilterFormData, getSchema, PAYLOAD_DEFAULT, PAYLOAD_PAGINATION } from './config';

type TramiteSolicitudesListadoProps = {
	readonly goToDetail: (rowData: ITramiteListado) => void;
	readonly idTipoTramite: number;
};

type Pagination = Pick<ITramitesListadoPayload, 'limit' | 'start'>;

const TramiteSolicitudesListado = ({ goToDetail, idTipoTramite }: TramiteSolicitudesListadoProps): ReactElement => {
	const filterBarRef = useRef<CTNFilterBarRef>(null);
	const {
		maestrosGeneric: { tipoTramite: tipoTramiteMaestro },
		maestrosTramite: { estadoTramite, estadoRevisionFirma },
	} = useGlobalContext();
	const location = useLocation();
	const { navigateData, setNavigateData } = usePageContext();
	const [isReady, setIsReady] = useState(false);

	const tableRef = useRef<CTNTableComponentRowRef<ITramiteListado>>(null);
	const { loading, triggerFetch } = useApiData({ loading: ['listado', 'eliminar'] as const });
	const [dataTable, setDataTable] = useState<ITramitesListadoResponse>({ totalResults: 10, listado: [] });
	const [pagination, setPagination] = useState<Pagination>(PAYLOAD_PAGINATION);
	const isInitialLoad = useRef(true);

	const { watch, control, reset, setValue } = useForm<FilterFormData>({
		resolver: getSchema(),
		mode: 'onChange',
		defaultValues: defaultValues(idTipoTramite),
	});

	const search = watch('search');
	const tipoBusqueda = search?.[0]?.key ?? defaultValues(idTipoTramite).tipoBusqueda;
	const listaIdsTipos = watch('listaIdsTipos');
	const listaIdsEstados = watch('listaIdsEstados');
	const listaIdsEstadosSubsanacionFirma = watch('listaIdsEstadosSubsanacionFirma');
	useEffect(() => {
		if (!navigateData?.filter?.urls?.some(path => location.pathname.includes(path))) {
			const { filter, ...resto } = navigateData ?? {};
			setNavigateData(resto);
			getData(mapPayload(pagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsEstadosSubsanacionFirma }));
			setIsReady(true);
			return;
		}

		const {data} = navigateData.filter;
		if (!data) {
			getData(mapPayload(pagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsEstadosSubsanacionFirma }));
			setIsReady(true);
			return;
		}
		reset({
			...defaultValues,
			search: [{ key: data.tipoBusqueda, search: data.numeroTramite }],
			tipoBusqueda: data.tipoBusqueda,
			busqueda: data.numeroTramite,
			listaIdsEstados: (data.listaIdsEstados ?? []).map((id: number) => String(estadoTramite?.find((e) => e.id === id)?.id)),
			listaIdsEstadosSubsanacionFirma: (data.listaIdsEstadosSubsanacionFirma ?? []).map((id: number) => String(estadoRevisionFirma?.find((e) => e.id === id)?.id))
		});
		filterBarRef.current?.addFilters({
			search: data.numeroTramite ? [{ key: data.tipoBusqueda ?? defaultValues(idTipoTramite).tipoBusqueda, search: data.numeroTramite ?? '' }] : [],
			tipoBusqueda: data.tipoBusqueda,
			busqueda: data.numeroTramite,
			listaIdsEstados: (data.listaIdsEstados ?? []).map((id: number) => String(id)),
			listaIdsEstadosSubsanacionFirma: (data.listaIdsEstadosSubsanacionFirma ?? []).map((id: number) => String(id)),
		});
		const { filter, ...resto } = navigateData ?? {};
		setNavigateData(resto);
		setIsReady(true);
		isInitialLoad.current = false;
	}, []); 

	const getData = async (payload: Pagination & Partial<ITramitesListadoPayload>) => {
		const { data } = await triggerFetch({
			fetch: postOperations,
			payload: { ...PAYLOAD_DEFAULT, ...payload },
			name: 'listado',
		});
		if (data) {
			setDataTable(data);
		}
	};

	const mapPayload = (pagination: Pagination, filter: FilterFormData): ITramitesListadoPayload => ({
		...pagination,
		[tipoBusqueda]: filter.busqueda,
		listaIdsTipos: filter.listaIdsTipos?.length > 0 ? filter.listaIdsTipos.map(Number) : undefined,
		listaIdsEstados: filter.listaIdsEstados?.length > 0 ? filter.listaIdsEstados.map(Number) : undefined,
		listaIdsEstadosSubsanacionFirma: filter.listaIdsEstadosSubsanacionFirma?.length > 0 ? filter.listaIdsEstadosSubsanacionFirma.map(Number) : undefined,
	});

	const onPageChange = (start: number) => {
		let newPagination = {
			start: PAGE_SIZE.OPERACIONES * (start - 1),
			limit: PAGE_SIZE.OPERACIONES,
		};

		if (start === 1) {
			newPagination = {
				start: 0,
				limit: PAGE_SIZE.OPERACIONES,
			};
		}
		getData(mapPayload(newPagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsEstadosSubsanacionFirma }));
		setPagination(newPagination);
	};

	useEffect(() => {
		if (!isReady) return;
		if (isInitialLoad.current) {
			isInitialLoad.current = false;
			return;
		}
		const payload = mapPayload(pagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsEstadosSubsanacionFirma });
		getData(payload);
		setNavigateData({ filter: { urls: [location.pathname], data: payload }});
	}, [isReady, search, tipoBusqueda, JSON.stringify(listaIdsTipos), JSON.stringify(listaIdsEstados), JSON.stringify(listaIdsEstadosSubsanacionFirma)]);

	return (
		<section className="u-mt-24">
			<CTNFilterBar
				ref={filterBarRef}
				tagBar
				show
				className="c-pnp-filter-bar c-pnp-filter-bar--wrap"
				control={control as any}
				setValue={setValue as any}
				reset={reset as any}
			>
				<CTNFilterSearch
					name="search" placeholder="Nº solicitud" label="Buscar"
					tag="Búsqueda" options={[{ label: 'Nº solicitud', value: 'numeroTramite' }]}/>
				<CTNFilterDropdown
					disabled
					multiple
					showArrow={false}
					label="Tipo de negocio jurídico"
					valueFormatter={() => 'Tipo de negocio jurídico'}
					placeholder="Tipo de negocio jurídico"
					name="tipoTramite"
					tag="Tipo de negocio jurídico"
					options={tipoTramiteMaestro?.map(({ descripcion, id }) => ({
						label: descripcion,
						value: String(id),
					}))}
				/>
				<CTNFilterDropdown
					multiple
					selectAll
					showArrow={false}
					label="Estado"
					valueFormatter={() => 'Estado'}
					placeholder="Estado"
					name="listaIdsEstados"
					tag="Estado"
					options={estadoTramite?.map((estado: IMaestro) => ({
						label: estado.descripcion,
						value: String(estado.id),
					}))}
				/>
				<CTNFilterDropdown
					multiple
					selectAll
					showArrow={false}
					label="Revisión de escritura"
					valueFormatter={() => 'Revisión de escritura'}
					placeholder="Revisión de escritura"
					name="listaIdsEstadosSubsanacionFirma"
					tag="Estado revisión"
					options={estadoRevisionFirma?.map((estado: IMaestro) => ({
						label: estado.descripcion,
						value: String(estado.id),
					}))}
				/>
				<CTNFilterButton
					name="clearAll"
					label="Reiniciar filtros"
					tag="Limpiar"
					icon={[{ icon: 'restartCircle', position: 'left', color: 'blue-60' }]}
					onClick={() => filterBarRef.current?.resetFilter()}
				/>
			</CTNFilterBar>
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 ]">
				<div className="c-pnp-box__body">
					<CTNTableComponentRow
						ref={tableRef}
						manualPagination
						renderSubComponent={renderSubComponent}
						idData="id"
						loading={loading.listado}
						loadingConf={{ spinner: true }}
						columns={getColumns({ goToDetail, tipoTramite: tipoTramiteMaestro, estadoRevisionFirma })}
						data={dataTable.listado}
						totalData={dataTable.totalResults}
						manualPageCount={dataTable.totalResults / PAGE_SIZE.OPERACIONES}
						pageSize={PAGE_SIZE.OPERACIONES}
						className="c-pnp-table"
						onPageChange={onPageChange}
					/>
				</div>
			</div>
		</section>
	);
};

export default TramiteSolicitudesListado;
